import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import sfRegular from "../fonts/SF-Pro-Display-Regular.ttf";
import sfMedium from "../fonts/SF-Pro-Display-Medium.ttf";

/*
  Turn one element into a PDF the browser downloads.

  Option B of the three we weighed. Unlike window.print() this needs no dialog —
  the file arrives the way any download does — and unlike a hand-built PDF it
  photographs the page we already have, so the document cannot drift away from
  the design the way a second, parallel layout would.

  The cost, stated plainly because it does not show up until someone tries to
  use the file: the page becomes an IMAGE. The figures are not selectable, not
  searchable, and go soft past about 150% zoom.

  ---- how it is put together ----

  html2canvas rasterises the element at `SCALE` device pixels per CSS pixel, so
  the text is sharp at normal viewing size. That one tall canvas is then cut
  into page-sized bands and each band drawn onto its own PDF page.

  Each band is drawn into a page-sized scratch canvas at a negative offset,
  rather than handing jsPDF the whole image and shifting it up the page each
  time. The second approach embeds the entire image once per page, multiplying
  the file size by the page count.
*/

// Device pixels per CSS pixel. 2 is sharp on screen without quadrupling weight.
const SCALE = 2;
// JPEG rather than PNG: a screenshot of a page compresses to a fraction of it.
const QUALITY = 0.92;
/*
  A4 portrait, in mm.

  The report is a 1248px-wide layout either way, so the paper's WIDTH is what
  sets the scale of everything in it: that width, less the margins, is what the
  1248px is drawn into. Portrait gives it 194mm where landscape gave it 281, so
  the whole document is drawn about 31% smaller — 13px body copy lands near 5.7pt
  rather than 8.3pt.

  What it buys is height: a portrait page holds a little over twice as much of
  the report as a landscape one, so far fewer blocks are pushed whole onto a page
  of their own.
*/
const PAGE = { w: 210, h: 297, margin: 8 };

/*
  The closing strip, in millimetres, drawn at the foot of EVERY page.

  It is not part of the captured element. It cannot be: the element is
  photographed once, as a single tall image, so anything inside it appears
  exactly once — at the very end of the document — and its page counter would
  have nothing to count. Drawn here instead, after the slicing loop has settled
  how many pages there are, each page gets its own footer and its own number.

  Being drawn rather than photographed, it is also real text: selectable,
  searchable, and sharp at any zoom, unlike the report above it.

  ---- why it is this small ----

  Every millimetre it takes is a millimetre the report cannot use, and the
  report is paginated in whole blocks — a section that no longer fits does not
  shrink, it moves to the next page and leaves the rest of the current one
  blank. A footer that costs 11mm of the printable area cost far more than 11mm
  of paper.

  So it lives in the bottom margin rather than above it. `drop` is the strip at
  the foot of the sheet the footer owns, margin included; the content runs to
  the top of it, costing it 4mm rather than the 11 a band above the margin did.

  These sizes are absolute, not scaled with the report: the strip is a caption on
  the paper rather than part of the document, and it has to stay legible at
  whatever scale the page is drawn at.
*/
const FOOTER = {
  // Foot of the content to the foot of the paper — the whole strip.
  drop: 12,
  // Content to the hairline.
  gap: 2.5,
  // Hairline to the first baseline, then baseline to baseline.
  lead: 3.1,
  line: 2.2,
  size: 5,
  // 0.04em of tracking, the design's, expressed in mm at this size.
  tracking: 0.07,
  ink: [19, 43, 82],
  muted: [91, 107, 133],
  hair: [237, 240, 244],
};

/*
  The width the report is captured at, matching what `.capturing` pins the live
  element to. Fixed rather than taken from the window so the PDF is the same
  document whatever size the browser happens to be — a narrow window would
  otherwise produce a narrow-screen PDF.
*/
const LAYOUT_WIDTH = 1248;

/*
  Charts come out in a serif face unless the font is put inside them.

  html2canvas does not draw an <svg> the way it draws the rest of the page: it
  serialises the element and hands the string to an <img>. An SVG loaded that
  way is its own isolated document — the page's stylesheet does not reach it, so
  the `font-family` its <text> was inheriting from <body> is simply gone, and
  what is left is the SVG default, which is serif. That is the Times-looking
  axis labels on every chart in the report.

  Naming a font on the SVG root is not enough either — tried, and the labels
  came back serif regardless, because the isolated document has no @font-face to
  resolve the name against and cannot fetch one. So the face travels with the
  SVG: the same two files jsPDF is given, inlined as data URIs in a <style>
  inside each chart, at the weights App.css declares for them.

  None of this reaches the PDF. The SVG is rasterised on the way in and only the
  pixels are kept, so the fonts cost capture time, not file size.

  Only charts that draw text get it; a donut is all paths and needs nothing.
*/
const embedSvgFont = (root, faces) => {
  if (!faces) return;
  const doc = root.ownerDocument;
  const css = faces
    .map(
      (face) =>
        `@font-face{font-family:"SF Pro Display";font-weight:${face.weight};` +
        `font-style:normal;src:url(data:font/ttf;base64,${face.base64}) ` +
        `format("truetype");}`
    )
    .join("");

  root.querySelectorAll("svg").forEach((svg) => {
    if (!svg.querySelector("text")) return;
    const style = doc.createElementNS("http://www.w3.org/2000/svg", "style");
    style.textContent = css;
    svg.insertBefore(style, svg.firstChild);
    // On the root, so every <text> inside inherits it as it did from <body>.
    svg.setAttribute("font-family", '"SF Pro Display"');
  });
};

/*
  Where the pages are cut.

  The same rule the browser's own print engine follows, and the same one option
  A's print stylesheet expressed: fill each page to the bottom, but never let a
  break land INSIDE something that has to be read as a unit — a summary tile, a
  key ratio, a beneficiary card, a chart beside its table, a table row.

  A section is therefore allowed to continue overleaf, which is what keeps the
  pages full. Refusing to split sections at all was tidier per section but left
  a third of a sheet empty behind each one, and put the header card alone on
  page 1.

  The list is deliberately the same one the report's print stylesheet gives to
  `break-inside: avoid`, so the two routes paginate alike.

  ---- why the selectors look like this ----

  These are CSS-module classes, so the browser never sees `.ratio`: the build
  rewrites it to `AssetDashboard_ratio__ZE2yY`. A plain `.ratio` here matches
  NOTHING once compiled, which is exactly what happened — only `tr` and `svg`,
  being element selectors, survived, so tables and charts were protected while
  the ratio rows were cut in half.

  Matching on the substring `name__` catches the hashed form and cannot catch a
  longer name that merely starts the same way: `stat__` is in
  `AssetDashboard_stat__LZvt3` and not in `AssetDashboard_stat_label__…`.

  Measured on html2canvas's CLONE rather than the live page: the clone is laid
  out in its own iframe, and if that iframe's width crosses one of the report's
  breakpoints its heights are not the live ones, so measuring the thing actually
  being photographed is the only way the offsets can be trusted.
*/
const UNBREAKABLE = [
  "[class*='stat__']",
  "[class*='ratio__']",
  "[class*='step__']",
  "[class*='share_card__']",
  "[class*='alloc_row__']",
  "[class*='legend__']",
  "[class*='bar_row__']",
  "tr",
  "svg",
];

/*
  A heading is never the last thing on a page — it would announce a block that
  only appears overleaf. Rather than a rule of its own, each one simply claims
  a little of the space beneath it, so a cut cannot fall in the gap between a
  label and what it labels.
*/
const HEADING = "[class*='block_label__'], [class*='section_head__']";
const HEADING_LEAD = 3;

const forbiddenBands = (root) => {
  const top = root.getBoundingClientRect().top;
  const bands = [];
  const add = (el, lead) => {
    const rect = el.getBoundingClientRect();
    if (!rect.height) return;
    bands.push({
      top: Math.round((rect.top - top) * SCALE),
      bottom: Math.round((rect.bottom - top + rect.height * lead) * SCALE),
    });
  };
  root.querySelectorAll(UNBREAKABLE.join(",")).forEach((el) => add(el, 0));
  root.querySelectorAll(HEADING).forEach((el) => add(el, HEADING_LEAD));
  return bands;
};

const cutAt = (bands, from, pageHeight, total) => {
  const limit = from + pageHeight;
  if (limit >= total) return total;

  // Walk the cut up out of every band it lands inside, until it is clear.
  let cut = limit;
  for (let pass = 0; pass < bands.length + 1; pass += 1) {
    let moved = false;
    for (const band of bands) {
      if (cut > band.top && cut < band.bottom) {
        cut = band.top;
        moved = true;
      }
    }
    if (!moved) break;
  }

  // Something taller than a whole page: there is nowhere clear to cut it.
  return cut > from ? cut : limit;
};

/*
  The site's own typeface, embedded so the drawn footer is set in the same face
  as the photographed report above it rather than in jsPDF's built-in Helvetica.

  The two files are the ones `App.css` already serves to the browser, imported
  for their URL and fetched when a report is first generated — not inlined into
  the bundle, which would put 340KB of font in front of every page load for the
  sake of one line at the foot of a PDF. They are read once per session.

  The family ships a Regular and a Medium and no Bold, so Medium stands in for
  bold. That is what the site itself has to work with: `font-weight: 700` on the
  brand resolves to the same Medium file, which the browser then thickens on its
  own.
*/
const FONT = {
  family: "SFProDisplay",
  // `weight` is what App.css declares for the same file; `style` is jsPDF's name.
  faces: [
    {
      url: sfRegular,
      file: "SFProDisplay-Regular.ttf",
      style: "normal",
      weight: 400,
    },
    {
      url: sfMedium,
      file: "SFProDisplay-Medium.ttf",
      style: "bold",
      weight: 500,
    },
  ],
};

/*
  btoa() takes a string, and String.fromCharCode applied to a whole font blows
  the argument limit, so the bytes go over in chunks.
*/
const toBase64 = (bytes) => {
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return window.btoa(binary);
};

/*
  The two files are subsets — 187 characters apiece — and what they leave out is
  not what you would guess. Latin-1 and the accents are all there; the em and en
  dashes, curly quotes, the ellipsis and the euro sign are not, and neither is
  the plain ASCII HYPHEN, the one character of printable ASCII missing from
  either file.

  A character the font cannot draw does not come out as a missing-glyph box.
  Both failures were seen: the em dash silently vanished, and the hyphen cut the
  line short where it stood — "OSTREON - WEALTH HERITAGE" printed as "OSTREON".
  That is how this was found, and it is why the text is filtered rather than
  trusted.

  The page itself is unaffected: it is photographed by the browser, which falls
  back to another font per missing glyph. Only what jsPDF draws, with one font
  and no fallback, has to stay inside the subset.
*/

// Tried first, so a mark that has a sensible equivalent keeps its meaning.
const SUBSTITUTE = {
  "‘": "'",
  "’": "'",
  "“": '"',
  "”": '"',
  "…": "...",
  " ": " ",
  " ": " ",
  "€": "EUR",
};

/*
  What a face can actually draw, read out of its own cmap — guessing at the
  coverage is what produced the truncated line above.

  Formats 4 and 12 are the two a modern TTF uses; anything else returns null and
  the caller falls back to substitution alone.
*/
const coveredBy = (bytes) => {
  try {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    let cmap = 0;
    const tableCount = view.getUint16(4);
    for (let i = 0; i < tableCount; i += 1) {
      const record = 12 + 16 * i;
      const tag = String.fromCharCode(
        bytes[record],
        bytes[record + 1],
        bytes[record + 2],
        bytes[record + 3]
      );
      if (tag === "cmap") cmap = view.getUint32(record + 8);
    }
    if (!cmap) return null;

    // A Unicode subtable, preferring the fullest one on offer.
    let sub = 0;
    const subCount = view.getUint16(cmap + 2);
    for (let i = 0; i < subCount; i += 1) {
      const record = cmap + 4 + 8 * i;
      const platform = view.getUint16(record);
      const encoding = view.getUint16(record + 2);
      const unicode =
        (platform === 3 && (encoding === 1 || encoding === 10)) ||
        platform === 0;
      if (unicode) sub = cmap + view.getUint32(record + 4);
    }
    if (!sub) return null;

    const covered = new Set();
    const format = view.getUint16(sub);
    if (format === 4) {
      const segments = view.getUint16(sub + 6) / 2;
      const ends = sub + 14;
      const starts = ends + segments * 2 + 2;
      for (let i = 0; i < segments; i += 1) {
        const from = view.getUint16(starts + i * 2);
        const to = view.getUint16(ends + i * 2);
        if (from === 0xffff) continue;
        for (let c = from; c <= to; c += 1) covered.add(c);
      }
    } else if (format === 12) {
      const groups = view.getUint32(sub + 12);
      for (let i = 0; i < groups; i += 1) {
        const group = sub + 16 + i * 12;
        const from = view.getUint32(group);
        const to = view.getUint32(group + 4);
        // Guard against a pathological table claiming the whole plane.
        for (let c = from; c <= to && c - from < 0x10000; c += 1) covered.add(c);
      }
    } else {
      return null;
    }
    return covered;
  } catch (error) {
    return null;
  }
};

/*
  The text as the embedded faces can actually draw it: the marks above folded to
  their equivalents, then anything still outside the coverage turned into a
  space. A space rather than nothing, so a word boundary the dropped character
  was carrying does not close up — and a space is in every subset.
*/
const drawable = (text, covered) => {
  const folded = String(text == null ? "" : text).replace(
    /[‘’“”…  €]/g,
    (mark) => SUBSTITUTE[mark]
  );
  if (!covered) return folded;
  let out = "";
  for (const character of folded) {
    out += covered.has(character.codePointAt(0)) ? character : " ";
  }
  return out;
};

// Fetched once and kept; null until then, false once a fetch has failed.
let faceCache = null;

const loadFaces = async () => {
  if (faceCache !== null) return faceCache;
  try {
    faceCache = await Promise.all(
      FONT.faces.map(async (face) => {
        const response = await fetch(face.url);
        if (!response.ok) throw new Error(`font ${response.status}`);
        const bytes = new Uint8Array(await response.arrayBuffer());
        return { ...face, base64: toBase64(bytes), covered: coveredBy(bytes) };
      })
    );
  } catch (error) {
    // A footer in Helvetica is worth more than no report at all.
    faceCache = false;
  }
  return faceCache;
};

/*
  The closing strip is shaped { brand, note: [line, line], page(n, total) }. The
  strings are the caller's because they are translated, and this file has no
  business reaching into the language context.
*/
const stampFooter = (pdf, footer, faces) => {
  if (faces) {
    faces.forEach((face) => {
      pdf.addFileToVFS(face.file, face.base64);
      pdf.addFont(face.file, FONT.family, face.style);
    });
  }
  const family = faces ? FONT.family : "helvetica";
  /*
    What EVERY face can draw, not what one of them can: the brand is set in one
    file and the note in the other, and a mark only one of them carries would
    come out of the wrong one broken.
  */
  const covered =
    faces && faces.every((face) => face.covered)
      ? new Set(
          [...faces[0].covered].filter((point) =>
            faces.every((face) => face.covered.has(point))
          )
        )
      : null;

  const total = pdf.getNumberOfPages();
  const left = PAGE.margin;
  const right = PAGE.w - PAGE.margin;
  const ruleY = PAGE.h - FOOTER.drop + FOOTER.gap;
  const base = ruleY + FOOTER.lead;

  for (let page = 1; page <= total; page += 1) {
    pdf.setPage(page);

    pdf.setDrawColor(...FOOTER.hair);
    pdf.setLineWidth(0.2);
    pdf.line(left, ruleY, right, ruleY);

    pdf.setFontSize(FOOTER.size);
    pdf.setFont(family, "bold");
    pdf.setTextColor(...FOOTER.ink);
    /*
      Tracking on the brand only. jsPDF measures a string for `align: "right"`
      without the character spacing, so a tracked right-aligned line overhangs
      the margin by however much the tracking added.
    */
    pdf.setCharSpace(FOOTER.tracking);
    pdf.text(drawable(footer.brand, covered).toUpperCase(), left, base);
    pdf.setCharSpace(0);
    pdf.text(drawable(footer.page(page, total), covered), right, base, {
      align: "right",
    });

    pdf.setFont(family, "normal");
    pdf.setTextColor(...FOOTER.muted);
    (footer.note || []).forEach((line, i) => {
      pdf.text(drawable(line, covered), PAGE.w / 2, base + i * FOOTER.line, {
        align: "center",
      });
    });
  }
};

/*
  `captureClass` is the class that turns the element into the document: it pins
  the layout width, unfolds any section the reader had collapsed, takes the
  toggles out of the shot and reveals the issue date.

  It is put on the CLONE, never on the live element. Putting it on the live one
  meant the reader watched the page rearrange itself under the cursor when they
  pressed the button and rearrange back when the file arrived — sections opening,
  the header card reflowing around a date that only belongs in the PDF.

  It works because html2canvas measures the CLONE, not the original: `onclone`
  runs inside `toIFrame`, and the bounds that size the canvas are read from the
  cloned element afterwards. So a clone that is taller than what is on screen —
  which it is, the moment a collapsed section unfolds — is captured in full.
*/
export const downloadElementPdf = async (
  node,
  filename = "report.pdf",
  { footer = null, captureClass = "", omitSelector = "" } = {}
) => {
  if (!node) return;

  /*
    Loaded before the capture, not after: the charts need the same faces inlined
    into them while the clone is being built, and the footer needs them at the
    end. Fetched once per session either way.
  */
  const faces = await loadFaces();

  let bands = [];
  const canvas = await html2canvas(node, {
    scale: SCALE,
    useCORS: true,
    backgroundColor: "#ffffff",
    windowWidth: LAYOUT_WIDTH,
    width: LAYOUT_WIDTH,
    scrollX: 0,
    scrollY: 0,
    onclone: (_doc, clone) => {
      // First, so the selects and the bands are measured on the final layout.
      if (captureClass) clone.classList.add(captureClass);
      /*
        Removed rather than hidden, and removed before anything is measured: a
        hidden section still occupies its margins, and a page break computed
        around a section that is not going to be drawn would leave a gap where
        it used to be.
      */
      if (omitSelector) {
        clone.querySelectorAll(omitSelector).forEach((el) => el.remove());
      }
      embedSvgFont(clone, faces);
      bands = forbiddenBands(clone);
    },
  });

  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const printW = PAGE.w - PAGE.margin * 2;
  // The report stops at the top of the footer's strip, or at the margin.
  const printH = PAGE.h - PAGE.margin - (footer ? FOOTER.drop : PAGE.margin);

  const pxPerMm = canvas.width / printW;
  const pageHeightPx = Math.floor(printH * pxPerMm);

  const slice = document.createElement("canvas");
  slice.width = canvas.width;
  const ctx = slice.getContext("2d");

  let y = 0;
  let first = true;
  while (y < canvas.height) {
    const cut = cutAt(bands, y, pageHeightPx, canvas.height);
    const h = cut - y;
    if (h <= 0) break;
    slice.height = h;
    // Fresh white behind every band; a short band would otherwise be black.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, slice.width, h);
    ctx.drawImage(canvas, 0, -y);

    if (!first) pdf.addPage();
    pdf.addImage(
      slice.toDataURL("image/jpeg", QUALITY),
      "JPEG",
      PAGE.margin,
      PAGE.margin,
      printW,
      h / pxPerMm
    );
    first = false;
    y = cut;
  }

  if (footer) stampFooter(pdf, footer, faces);

  pdf.save(filename);
};
